import { NavigateNext } from "@mui/icons-material";
import { Breadcrumbs } from "@mui/material";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GetOneWorkSpace } from "../../../services/WorkSpaceServices";
import { WorkSpaceUpdate } from "../../../model/WorkSpaceModel";
import { GetOneGroup } from "../../../services/GroupsServices";
import { Group } from "../../../model/GroupModel";
import { useUrlChangeListener } from "../../../hooks/useUrlChangeListener";
import { GetOneItem } from "../../../services/Item/ItemServicesGet";
import { WorkSpaceUserName } from "../../../model/ItemModel";
import { useTranslation } from "react-i18next";
import { BreadcrumbLink } from "./BreadcrumbLink";
import LoaderComp from "../../Loader/LoaderComp";
import { GetOneVr } from "../../../services/ValidationRuleService";
import { ValidationRuleVO } from "../../../model/ValidationRuleModel";

interface IKeyDriveBreadcrumb {
  title: string;
  path: string;
}

export function KeyDriveBreadcrumb() {
  const location = useUrlChangeListener();
  const { nameOrg, idWorkspace, idGroup, idFolder, idFile, idValidationRule } = useParams();
  const [workspace, setWorkspace] = useState<WorkSpaceUpdate>();
  const [file, setFile] = useState<WorkSpaceUserName>();
  const [folderChain, setFolderChain] = useState<WorkSpaceUserName[]>([]);
  const folderCache = useRef<Record<string, WorkSpaceUserName>>({});
  const latestFolderId = useRef<string>();
  const [validationRule, setValidationRule] = useState<ValidationRuleVO>();
  const [group, setGroup] = useState<Group>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loadingFolder, setLoadingFolder] = useState<boolean>(false);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);
  const [loadingValidationRule, setLoadingValidationRule] = useState<boolean>(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState<boolean>(false);

  const pathnames = useMemo(() => location.pathname.split('/').filter((x) => x), [location.pathname]);
  const excludeMapBreadcrumb = ["adminConsole", "wsallowed", "Item", "WorkSpace"];
  const path = useMemo(() => pathnames.filter(x => !excludeMapBreadcrumb.includes(x)), [pathnames]);

  const listPathsCommon = useMemo((): IKeyDriveBreadcrumb[] => [
    { title: t("Sidebar.Home"), path: nameOrg ?? "" },
    { title: "Workspaces", path: "workspaces" },
    { title: t("AdminConsole.UsersAndGroups"), path: "usersAndGroups" },
    { title: t("AdminConsole.AccountAndSubscription"), path: "accountAndSubscription" },
    { title: t("AdminConsole.NewUser"), path: "newUser" },
    { title: t("AdminConsole.NewGroup"), path: "newGroup" },
    { title: t("Configuracoes.message"), path: "config" },
    { title: t("Sidebar.Dashboard"), path: "dashboard" },
    { title: t("MyData.Mydata"), path: "mydata" },
    { title: t("RecicleBin.title"), path: "recycleBin" },
    { title: "Resultados", path: "allResults" },
    { title: t("Sidebar.Groups"), path: "groups" },
    { title: t("Sidebar.Users"), path: "users" },
    { title: t("Upload.permission"), path: "Permission" },
    { title: t("Sidebar.Temporality"), path: "temporality" },
    { title: "Tipos de Documento", path: "documentTypes" },
    { title: t("Sidebar.ValidationRules"), path: "validationRule" },
    { title: t("Feed.Updates"), path: "tracker" },
    { title: "Arquivo", path: "file" },
    { title: t("Sidebar.Pendencies"), path: "UserPendencies" },
    { title: t("Upload.permission"), path: "PermissionUser" },
    { title: t("Sidebar.Preferences"), path: "preferences" },
    { title: t("New.female"), path: "new" },
    { title: t("AdminConsole.GuestUserAdd"), path: "newGuestUser" },
    { title: t("SharedItem.Title"), path: "SharedItem" },
    { title: t("AdminConsole.Plans"), path: "plans" },
    { title: t("AdminConsole.NewUserBatch"), path: "batchNewUser" },
    { title: t("AdminConsole.Organizations"), path: "organizations" },
    { title: "Log de Erros", path: "logErrors" },
  ], [t, nameOrg]);

  const toTitleCase = useCallback((str: string) => {
    return listPathsCommon.find(x => x.path === str)?.title ?? str;
  }, [listPathsCommon]);

  const getFolder = async (id: string): Promise<WorkSpaceUserName | undefined> => {
    if (folderCache.current[id]) return folderCache.current[id];

    try {
      const folder = await GetOneItem(id);
      if (folder) folderCache.current[id] = folder;
      return folder;
    } catch (err) {
      return undefined;
    }
  }

  const fetchFolderChain = async (folderId: string) => {
    const chain: WorkSpaceUserName[] = [];
    const visited = new Set<string>();
    let currentId: string | undefined = folderId;

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const current: WorkSpaceUserName | undefined = await getFolder(currentId);
      if (!current) break;
      chain.unshift(current);
      currentId = current.fatherId;
    }

    return chain;
  }

  const fetchFolder = () => {
    if (!idFolder) {
      latestFolderId.current = undefined;
      setFolderChain([]);
      return;
    }

    const requestedFolderId = idFolder;
    latestFolderId.current = requestedFolderId;

    setLoadingFolder(true);
    fetchFolderChain(requestedFolderId)
    .then(chain => {
      if (latestFolderId.current === requestedFolderId) setFolderChain(chain);
    })
    .catch(err => console.error('Error loading folder data:', err))
    .finally(() => {
      if (latestFolderId.current === requestedFolderId) setLoadingFolder(false);
    });
  }

  const fetchValidationRule  = () => {
    if (!idValidationRule) return;
    
    setLoadingValidationRule(true);
    GetOneVr(idValidationRule!)
    .then(data => {
      setValidationRule(data)
    })
    .catch(err => console.error('Error loading validation rule data:', err))
    .finally(() => setLoadingValidationRule(false));
  }


  const fetchGroup = () => {
    if (!idGroup) return;

    GetOneGroup(idGroup)
    .then(data => {
      setGroup(data)
    })
    .catch(err => console.error('Error loading group data:', err));
  }

  const fetchFile = () => {
    if (!idFile) return;

    setLoadingFile(true);
    GetOneItem(idFile!)
    .then(data => {
      setFile(data)
    })
    .catch(err => console.error('Error loading file data:', err))
    .finally(() => setLoadingFile(false));
  }

  useEffect(() => {
    if (idWorkspace) {
      setLoadingWorkspace(true)
      GetOneWorkSpace(idWorkspace).then((data) => {
        setWorkspace(data!);
      })
      .catch(err => console.error('Error loading workspace data:', err))
      .finally(() => setLoadingWorkspace(false))
    }

    fetchFolder();
    fetchFile();
    fetchValidationRule();
    fetchGroup();
  }, [idWorkspace, idFile, idFolder, idGroup, idValidationRule, path]);

  const renderBetterText = useCallback((input: string): string => {
    if (!input) return "";
    if (input === idGroup) return group?.name || t("AdminConsole.Group");
    if (input === idWorkspace) return workspace?.name || input;
    if (input === idFile) return file?.name || input;
    if (input === idFolder) return folderChain[folderChain.length - 1]?.name || input;
    if (input === idValidationRule) return validationRule?.name || input;
    return input;
  }, [idWorkspace, 
    idGroup, 
    workspace, 
    pathnames, 
    idFile, 
    idFolder, 
    file, 
    folderChain, 
    group, 
    idValidationRule, 
    validationRule, 
    t]);

  const breadcrumbItems = useMemo(() => {
    if (pathnames.includes("adminConsole") && pathnames.includes("workspaces") && pathnames.includes("config") && idWorkspace) {
      return [
        <BreadcrumbLink
          key="home"
          onClick={() => navigate(`/${nameOrg}/adminConsole`)}
          text={t("Sidebar.Home")}
        />,
        <BreadcrumbLink
          key="workspaces"
          onClick={() => navigate(`/${nameOrg}/adminConsole/workspaces`)}
          text="Workspaces"
        />,
        <BreadcrumbLink
          key="editing"
          onClick={() => {}}
          text={`Editando ${workspace?.name || idWorkspace}`}
        />
      ];
    }

    if (pathnames.includes("adminConsole")) {
      const items: JSX.Element[] = [];
      
      items.push(
        <BreadcrumbLink
          key="adminConsole"
          onClick={() => navigate(`/${nameOrg}/adminConsole`)}
          text={t("Sidebar.Home")}
        />
      );
      
      const adminIndex = pathnames.indexOf("adminConsole");
      const adminPath = pathnames.slice(adminIndex + 1);
      
      adminPath.forEach((value, index) => {
        const isLast = index === adminPath.length - 1;
        const to = `/${nameOrg}/adminConsole/${adminPath.slice(0, index + 1).join('/')}`;

        let displayText = toTitleCase(value);

        if (value === "newUser" && new URLSearchParams(location.search).get("userId")) {
          displayText = t("AdminConsole.EditInfo");
        }
        else if (value == idGroup) {
          displayText = renderBetterText(toTitleCase(value));
        }
        else if (value === idWorkspace && workspace?.name) {
          displayText = renderBetterText(toTitleCase(value));
        }
        else if (value === idValidationRule && validationRule?.name) {
          displayText = renderBetterText(toTitleCase(value));
        }

        items.push(
          <BreadcrumbLink
            key={`admin-${index}`}
            onClick={isLast ? () => {} : () => navigate(to)}
            text={displayText}
          />
        );
      });
      
      return items;
    }

    return path.flatMap((value, index) => {
      const isLast = index === path.length - 1;
      let to = `/${path.slice(0, index + 1).join('/')}`;

      if (path[index-1] === nameOrg && path[index] === idWorkspace) {
        to = `/${nameOrg}/wsallowed/${idWorkspace}`;
      }

      if (path[index] === idFolder && loadingFolder) {
        return (
          <LoaderComp key={`loader-${index}`} />
        )
      }

      if (path[index] === idFolder && folderChain.length > 0) {
        return folderChain.map((folderItem, folderIndex) => {
          const isCurrentFolder = folderIndex === folderChain.length - 1;

          return (
            <BreadcrumbLink
              key={`folder-${folderItem.id}`}
              onClick={isLast && isCurrentFolder ? () => {} : () => navigate(`/${nameOrg}/wsallowed/${idWorkspace}/${folderItem.id}`)}
              text={folderItem.name ?? ""}
            />
          );
        });
      }

      if (path[index] === idFolder) {
        to = `/${nameOrg}/wsallowed/${idWorkspace}/${idFolder}`;
      }

      if (path[index] === idFile && loadingFile) {
        return (
          <LoaderComp />
        )
      }

      if (path[index] === idValidationRule && loadingValidationRule) {
        return (
          <LoaderComp />
        )
      }

      return (
        <BreadcrumbLink
          key={`default-${index}`}
          onClick={(isLast || (path[index] == "itemDetail")) ? () => {} : () => navigate(to)}
          text={renderBetterText(toTitleCase(value))}
        />
      );
    });
  }, [pathnames, 
    location.search, 
    t, 
    path, 
    nameOrg, 
    idWorkspace, 
    idGroup, 
    idFile, 
    idValidationRule, 
    navigate, 
    renderBetterText, 
    toTitleCase, 
    loadingFolder, 
    loadingFile, 
    loadingWorkspace, 
    loadingValidationRule, 
    folderChain, 
    idFolder, 
    file, 
    validationRule]);

  return (
    <Breadcrumbs 
      separator={<NavigateNext style={{ opacity: "0.5" }} fontSize="small" />} 
      aria-label="breadcrumb" 
      style={{ color: "#22638D" }}
    >
      {breadcrumbItems}
    </Breadcrumbs>
  );
}